import React, { useEffect, useLayoutEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { createTask, updateTask } from '../services/api';
import { useTaskStore } from '../stores/taskStore';
import { useCustomerStore } from '../stores/customerStore';
import { Input, Textarea, CustomSelect, Label, FormField, FormError, LoadingButton, dialogButtonClass, DialogShell, DialogHeader, DialogFooter } from '@redpanda/ui';
import { toast } from 'react-hot-toast';
import { getTodayDateString } from '../utils/dateUtils';
import { canEditArchivedCustomerData } from '../utils/customerUtils';
import { notifyDataChanged } from '../utils/dataEvents';

interface Customer {
  id: string;
  name: string;
  email?: string;
  company?: string;
  archived?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface TaskDialogProps {
  isOpen: boolean;
  onClose: () => void;
  task?: {
    id: string;
    customerId: string;
    title: string;
    description?: string;
    dueDate: string;
    priority: string;
    status: string;
    customer: Customer;
  };
  preselectedCustomerId?: string;
}

interface FormData {
  customerId: string;
  title: string;
  description?: string;
  dueDate: string;
  priority: string;
}

const TaskDialog: React.FC<TaskDialogProps> = ({
  isOpen,
  onClose,
  task,
  preselectedCustomerId,
}) => {
  const { register, handleSubmit, formState: { errors }, setValue, reset, watch } = useForm<FormData>();

  const { updateTask: updateTaskInStore, addTask: addTaskToStore, replaceTask, removeTask } = useTaskStore();
  const { allCustomers, isLoadingAllCustomers, fetchAllCustomers } = useCustomerStore();
  const { t } = useTranslation();


  const isEditMode = !!task;
  
  // 檢查是否可以編輯（封存客戶的關聯資料不能編輯）
  const canEdit = !isEditMode || canEditArchivedCustomerData(task?.customer);
  
  // 監聽表單值變化
  const watchedCustomerId = watch('customerId', '');
  const watchedPriority = watch('priority', 'MEDIUM');

  // 載入客戶資料
  useEffect(() => {
    if (isOpen) {
      fetchAllCustomers();
    }
  }, [isOpen, fetchAllCustomers]);

  // 設置表單初始值（useLayoutEffect：繪製前帶入，編輯時不會先閃一格空白欄位）
  useLayoutEffect(() => {
    if (isOpen) {
      if (task) {
        // 編輯模式
        setValue('customerId', task.customerId);
        setValue('title', task.title);
        setValue('description', task.description || '');
        // 直接使用資料庫返回的日期（已經是正確格式）
        setValue('dueDate', task.dueDate.split('T')[0]);
        setValue('priority', task.priority);
      } else {
        // 新增模式
        reset();
        if (preselectedCustomerId) {
          setValue('customerId', preselectedCustomerId);
        }
        setValue('priority', 'MEDIUM');
        // 設置預設到期日為今天
        setValue('dueDate', getTodayDateString());
      }
    }
  }, [isOpen, task, preselectedCustomerId, setValue, reset]);


  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = async (data: FormData) => {
    // 檢查是否可以編輯封存客戶的資料
    if (isEditMode && !canEdit) {
      toast.error(t('errors.business.cannotEditArchivedCustomerData', '無法編輯封存客戶的關聯資料'));
      return;
    }

    const selectedCustomer = allCustomers.find(c => c.id === data.customerId);

    if (isEditMode) {
      // UPDATE：保存原始值以供回滾
      const originalTask = { ...task };

      // 樂觀更新 + 立即關閉
      updateTaskInStore(task.id, {
        customerId: data.customerId,
        title: data.title.trim(),
        description: data.description?.trim(),
        dueDate: `${data.dueDate}T00:00:00.000Z`,
        priority: data.priority,
        status: task.status,
        customer: selectedCustomer ? {
          id: selectedCustomer.id,
          name: selectedCustomer.name,
          email: selectedCustomer.email,
          company: selectedCustomer.company
        } : task.customer
      });
      handleClose();

      // 背景執行 API 更新
      updateTask(task.id, {
        customerId: data.customerId,
        title: data.title.trim(),
        description: data.description?.trim(),
        dueDate: `${data.dueDate}T00:00:00.000Z`,
        priority: data.priority,
      }).then(() => {
        toast.success(t('common.messages.updateSuccess'));
        notifyDataChanged('tasks'); // API 成功後才通知：客戶詳情任務區塊、儀表板、即將到期任務重新載入
      }).catch((error: any) => {
        console.error('任務更新失敗:', error);
        // 回滾到原始值
        updateTaskInStore(task.id, originalTask);
        toast.error(error.response?.data?.error || t('common.messages.updateFailed'));
      });
    } else {
      // CREATE：臨時 id 樂觀新增
      const tempId = `temp-${Date.now()}`;
      const optimisticTask = {
        id: tempId,
        customerId: data.customerId,
        title: data.title.trim(),
        description: data.description?.trim() || null,
        dueDate: `${data.dueDate}T00:00:00.000Z`,
        priority: data.priority,
        status: 'PENDING',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        customer: selectedCustomer ? {
          id: selectedCustomer.id,
          name: selectedCustomer.name,
          email: selectedCustomer.email,
          company: selectedCustomer.company
        } : undefined,
      };

      addTaskToStore(optimisticTask as any);
      handleClose();

      createTask({
        customerId: data.customerId,
        title: data.title.trim(),
        description: data.description?.trim(),
        dueDate: `${data.dueDate}T00:00:00.000Z`,
        priority: data.priority,
      }).then((response) => {
        if (response?.data) {
          replaceTask(tempId, response.data);
        }
        toast.success(t('common.messages.createSuccess'));
        notifyDataChanged('tasks');
      }).catch((error: any) => {
        console.error('任務建立失敗:', error);
        removeTask(tempId);
        toast.error(error.response?.data?.error || t('common.messages.createFailed'));
      });
    }
  };

  if (!isOpen) return null;

  return (
    <DialogShell onClose={handleClose} className="max-w-2xl">
      <DialogHeader title={isEditMode ? t('tasks.edit.title', { ns: 'pages' }) : t('tasks.add.title', { ns: 'pages' })} onClose={handleClose} closeLabel={t('common.actions.close')} />

      {/* 對話框內容 */}
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="p-6 space-y-6">
          {/* 封存客戶警告提示 */}
          {isEditMode && !canEdit && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                    {t('common.warnings.archivedCustomerData')}
                  </h3>
                  <div className="mt-2 text-sm text-yellow-700 dark:text-yellow-300">
                    <p>{t('common.warnings.archivedCustomerDataDetail')}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
          {/* 客戶選擇 */}
          <FormField>
            <Label htmlFor="customerId">{t('tasks.form.customer', { ns: 'pages' })} *</Label>
            <CustomSelect
              id="customerId"
              {...register('customerId', { required: t('tasks.form.selectCustomer', { ns: 'pages' }) })}
              value={watchedCustomerId}
              onChange={(value) => setValue('customerId', value)}
              variant={errors.customerId ? "error" : "default"}
              disabled={isLoadingAllCustomers || !canEdit}
              placeholder={t('common.forms.placeholders.selectCustomer')}
              options={[
                ...(isLoadingAllCustomers ? [{ value: 'loading', label: t('common.status.loading'), disabled: true }] : []),
                ...allCustomers
                  .filter(customer => customer.archived !== true)
                  .sort((a, b) => new Date(b.createdAt || b.updatedAt || '').getTime() - new Date(a.createdAt || a.updatedAt || '').getTime())
                  .map(customer => ({ 
                    value: customer.id, 
                    label: `${customer.name}${customer.company ? ` (${customer.company})` : ''}` 
                  }))
              ]}
            />
            {errors.customerId && <FormError>{errors.customerId.message}</FormError>}
          </FormField>

          {/* 任務標題 */}
          <FormField>
            <Label htmlFor="title">{t('tasks.form.title', { ns: 'pages' })} *</Label>
            <Input
              type="text"
              id="title"
              {...register('title', { 
                required: t('tasks.form.title', { ns: 'pages' }),
                maxLength: { value: 200, message: '標題不能超過 200 字元' }
              })}
              variant={errors.title ? "error" : "default"}
              disabled={!canEdit}
            />
            {errors.title && <FormError>{errors.title.message}</FormError>}
          </FormField>

          {/* 任務描述 */}
          <FormField>
            <Label htmlFor="description">{t('tasks.form.description', { ns: 'pages' })}</Label>
            <Textarea
              id="description"
              {...register('description', {
                maxLength: { value: 1000, message: '描述不能超過 1000 字元' }
              })}
              rows={4}
              variant={errors.description ? "error" : "default"}
              disabled={!canEdit}
            />
            {errors.description && <FormError>{errors.description.message}</FormError>}
          </FormField>

          {/* 到期日期 */}
          <FormField>
            <Label htmlFor="dueDate">{t('tasks.form.dueDate', { ns: 'pages' })} *</Label>
            <div className="relative">
              <Input
                type="date"
                id="dueDate"
                {...register('dueDate', { required: t('tasks.form.dueDate', { ns: 'pages' }) })}
                variant={errors.dueDate ? "error" : "default"}
                className="cursor-pointer"
                disabled={!canEdit}
                onMouseDown={(e) => {
                  // 防止文字被選取
                  e.preventDefault();
                }}
                onClick={(e) => {
                  // 為整個輸入欄位加入點擊事件，開啟日期選擇器
                  const input = e.target as HTMLInputElement;
                  if (input.type === 'date' && input.showPicker) {
                    try {
                      input.showPicker();
                    } catch (error) {
                      // 如果 showPicker 失敗，讓瀏覽器處理預設行為
                      console.log('showPicker not supported or failed');
                    }
                  }
                }}
              />
            </div>
            {errors.dueDate && <FormError>{errors.dueDate.message}</FormError>}
          </FormField>

          {/* 優先級 */}
          <FormField>
            <Label htmlFor="priority">{t('tasks.form.priority', { ns: 'pages' })}</Label>
            <CustomSelect
              id="priority"
              {...register('priority', { required: t('tasks.form.priority', { ns: 'pages' }) })}
              value={watchedPriority}
              onChange={(value) => setValue('priority', value)}
              variant={errors.priority ? "error" : "default"}
              disabled={!canEdit}
              options={[
                { value: 'LOW', label: t('tasks.priority.LOW', { ns: 'pages' }) },
                { value: 'MEDIUM', label: t('tasks.priority.MEDIUM', { ns: 'pages' }) },
                { value: 'HIGH', label: t('tasks.priority.HIGH', { ns: 'pages' }) },
                { value: 'URGENT', label: t('tasks.priority.URGENT', { ns: 'pages' }) }
              ]}
            />
            {errors.priority && <FormError>{errors.priority.message}</FormError>}
          </FormField>
        </div>

        <DialogFooter>
          <button
            type="button"
            onClick={handleClose}
            className={dialogButtonClass.cancel}
          >
            {t('common.actions.cancel')}
          </button>
          <LoadingButton
            type="submit"
            isEditMode={isEditMode}
            disabled={isLoadingAllCustomers || !canEdit}
            t={t}
          />
        </DialogFooter>
      </form>
    </DialogShell>
  );
};

export default TaskDialog;